import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../modal/Modal';
import Button from '../button/Button';
import { Header } from '../texts/Texts';
import { removeAccents } from 'common/utils/removeAccents';
import { functionDescriptions, hfOnlyFunctions, legacyAliases, categoryLabels, FunctionCategory } from './functionDescriptions';
import { getLanguagePack } from './engine/i18n';
import styles from './FunctionReferenceModal.module.scss';

interface FunctionReferenceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (functionName: string) => void;
}

interface FunctionEntry {
    localizedName: string;
    syntax: string;
    description: string;
    category: FunctionCategory;
}

const categoryOrder: FunctionCategory[] = [
    'math', 'text', 'date', 'logical', 'statistical',
    'financial', 'lookup', 'engineering', 'information', 'matrix',
];

const FunctionReferenceModal = ({ isOpen, onClose, onSelect }: FunctionReferenceModalProps) => {
    const { t, i18n } = useTranslation();
    const [search, setSearch] = useState('');
    const [selectedName, setSelectedName] = useState<string | null>(null);

    const lang = i18n.language as 'pt' | 'fr';

    const functions = useMemo(() => {
        const langPack = getLanguagePack(lang);
        const entries: FunctionEntry[] = [];

        for (const [englishKey, localizedName] of Object.entries(langPack.functions)) {
            if (!localizedName || localizedName === '') continue;
            if (hfOnlyFunctions.has(englishKey)) continue;
            if (legacyAliases.has(englishKey)) continue;

            const desc = functionDescriptions[englishKey];
            entries.push({
                localizedName,
                syntax: desc?.syntax ?? '',
                description: desc?.description?.[lang] ?? desc?.description?.pt ?? '',
                category: desc?.category ?? 'math',
            });
        }

        return entries;
    }, [lang]);

    const grouped = useMemo(() => {
        const normalizedSearch = removeAccents(search.trim()).toLowerCase();
        const filtered = normalizedSearch
            ? functions.filter(f =>
                removeAccents(f.localizedName).toLowerCase().includes(normalizedSearch) ||
                removeAccents(f.description).toLowerCase().includes(normalizedSearch)
            )
            : functions;

        const groups: Record<string, FunctionEntry[]> = {};
        for (const entry of filtered) {
            if (!groups[entry.category]) {
                groups[entry.category] = [];
            }
            groups[entry.category].push(entry);
        }

        for (const cat of Object.keys(groups)) {
            groups[cat].sort((a, b) =>
                removeAccents(a.localizedName).toLowerCase().localeCompare(
                    removeAccents(b.localizedName).toLowerCase()
                )
            );
        }

        return groups;
    }, [functions, search]);

    const totalCount = Object.values(grouped).reduce((sum, g) => sum + g.length, 0);

    const handleSave = () => {
        if (selectedName) {
            onSelect(selectedName);
        }
        onClose();
    };

    const handleCancel = () => {
        setSelectedName(null);
        setSearch('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={true}
            style={{ content: { minWidth: 450, maxWidth: 550 }, overlay: { zIndex: 300 } }}
            onRequestClose={handleCancel}
        >
            <div>
                <ModalHeader>
                    <Header>{t('common.function_reference')}</Header>
                </ModalHeader>
                <ModalBody>
                    <div className={styles.searchContainer}>
                        <input
                            className={styles.searchInput}
                            type="text"
                            placeholder={t('common.function_reference_search')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            autoFocus
                        />
                    </div>
                    <div className={styles.listContainer}>
                        {totalCount > 0
                            ? (
                                categoryOrder.map(cat => {
                                    const entries = grouped[cat];
                                    if (!entries || entries.length === 0) return null;
                                    const label = categoryLabels[cat]?.[lang] ?? categoryLabels[cat]?.pt ?? cat;
                                    return (
                                        <div key={cat}>
                                            <div className={styles.categoryHeader}>{label}</div>
                                            {entries.map((f, i) => (
                                                <div
                                                    key={`${cat}-${i}`}
                                                    className={`${styles.functionRow} ${selectedName === f.localizedName
                                                        ? styles.selectedRow
                                                        : ''}`}
                                                    onClick={() => setSelectedName(f.localizedName)}
                                                    onDoubleClick={() => {
                                                        onSelect(f.localizedName);
                                                        onClose();
                                                    }}
                                                >
                                                    <div>
                                                        <span className={styles.functionName}>{f.localizedName}</span>
                                                        {f.syntax && <span className={styles.functionSyntax}>{f.syntax}</span>}
                                                    </div>
                                                    {f.description && (
                                                        <div className={styles.functionDescription}>{f.description}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })
                            )
                            : (
                                <div className={styles.noResults}>
                                    {t('common.no_search_results')}
                                </div>
                            )}
                    </div>
                </ModalBody>
                <ModalFooter className="text-right">
                    <Button fw variant='secondary' onClick={handleCancel}>{t('common.cancel')}</Button>
                    <Button variant="primary" onClick={handleSave} disabled={!selectedName}>{t('common.insert')}</Button>
                </ModalFooter>
            </div>
        </Modal>
    );
};

export default FunctionReferenceModal;
